import axios from 'axios'
import {fetchExerciceByIdUrl} from '../redux/api'
import {getExerciceAttachmentUrl} from './file-helpers'

/**
 * Exercise image galleries in `/file-viewer`.
 * The viewer link only carries the exercise id and the image position; the viewer reloads the
 * attachments through GET /api/exercices/:id. Putting the gallery in the URL exceeds the proxy's
 * 8 KB request-line limit (HTTP 414) for large galleries with Arabic titles or file names.
 */

export const VIEWER_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']

export type ExerciseAttachment = {fileName: string; url?: string; path?: string}
export type ViewerGalleryFile = {url: string; name: string}

const OBJECT_ID = /^[a-f0-9]{24}$/i

export function isViewerImageFileName(fileName: string | undefined | null): boolean {
  const extension = String(fileName || '').split('.').pop()?.toLowerCase() || ''
  return VIEWER_IMAGE_EXTENSIONS.includes(extension)
}

/** Image attachments in stored order: the order the viewer and the click handlers share. */
export function exerciseImageAttachments<T extends ExerciseAttachment>(attachments: T[] | undefined | null): T[] {
  return (attachments || []).filter((att) => att && isViewerImageFileName(att.fileName))
}

export function isValidExerciseId(exerciseId: string | undefined | null): boolean {
  return OBJECT_ID.test(String(exerciseId || ''))
}

export function exerciseGalleryViewerPath(exerciseId: string, index: number): string {
  const position = Number.isFinite(index) && index > 0 ? Math.floor(index) : 0
  return `/file-viewer?exerciseId=${encodeURIComponent(exerciseId)}&index=${position}`
}

/** Single-file link (one URL and one name, so its length stays bounded). */
export function exerciseSingleFileViewerPath(exerciseId: string, attachment: ExerciseAttachment): string {
  return `/file-viewer?file=${encodeURIComponent(
    getExerciceAttachmentUrl(exerciseId, attachment)
  )}&fileName=${encodeURIComponent(attachment.fileName)}`
}

/** Opens the clicked attachment: images open the exercise gallery at that image, other files open alone. */
export function openExerciseAttachment(
  exerciseId: string,
  attachments: ExerciseAttachment[] | undefined | null,
  clicked: ExerciseAttachment
): void {
  if (!isViewerImageFileName(clicked.fileName) || !isValidExerciseId(exerciseId)) {
    window.open(exerciseSingleFileViewerPath(exerciseId, clicked), '_blank')
    return
  }
  const images = exerciseImageAttachments(attachments)
  let index = images.indexOf(clicked)
  if (index < 0) index = images.findIndex((att) => att.fileName === clicked.fileName)
  window.open(exerciseGalleryViewerPath(exerciseId, Math.max(index, 0)), '_blank')
}

export function openExerciseImageGallery(exerciseId: string, attachments: ExerciseAttachment[] | undefined | null): void {
  const first = exerciseImageAttachments(attachments)[0]
  if (first) openExerciseAttachment(exerciseId, attachments, first)
}

/** The viewer keys load state by name, so repeated names get a " (2)" suffix before the extension. */
function withUniqueNames(files: ViewerGalleryFile[]): ViewerGalleryFile[] {
  const seen = new Map<string, number>()
  return files.map((file) => {
    const count = (seen.get(file.name) || 0) + 1
    seen.set(file.name, count)
    if (count === 1) return file
    const dot = file.name.lastIndexOf('.')
    const name = dot > 0 ? `${file.name.slice(0, dot)} (${count})${file.name.slice(dot)}` : `${file.name} (${count})`
    return {...file, name}
  })
}

/** Gallery images of an exercise, rebuilt from GET /api/exercices/:id with the shared URL builder. */
export async function fetchExerciseGalleryFiles(exerciseId: string): Promise<ViewerGalleryFile[]> {
  if (!isValidExerciseId(exerciseId)) throw new Error('EXERCISE_GALLERY_INVALID_ID')
  const res = await axios.get(`${fetchExerciceByIdUrl}/${encodeURIComponent(exerciseId)}`)
  const attachments = res?.data?.exercice?.attachements
  if (!Array.isArray(attachments)) throw new Error('EXERCISE_GALLERY_UNAVAILABLE')
  return withUniqueNames(
    exerciseImageAttachments(attachments as ExerciseAttachment[]).map((att) => ({
      url: getExerciceAttachmentUrl(exerciseId, att),
      name: att.fileName,
    }))
  )
}
